import { Department } from "./departments";

export class Messages {
  private map: Map<string, string[]>;

  constructor(messages: Map<string, string[]>) {
    this.map = new Map();
    if (messages !== null && messages !== undefined && messages.size > 0) {
      messages.forEach((value: string[], key: string) => {
        if (value === null || value === undefined) {
          value = [];
        }
        this.map.set(key, value);
      });
    }
  }

  public static get EMPTY(): Messages {
    return new Messages(new Map());
  }

  public static orElse(
    error: any,
    fallbackKey: string,
    fallbackValue: string
  ): Messages {
    const messages = Messages.from(error);

    if (messages.hasAnyMessages) {
      return messages;
    }

    const fallback = new Map<string, string[]>();
    fallback.set(fallbackKey, [fallbackValue]);

    return new Messages(fallback);
  }

  public static from(arg: any) {
    const input = new Map<string, string[]>();
    // Only an object carries fields. A server that answers with plain text -
    // Jersey does, when it cannot bind a request body - used to be walked with
    // Object.keys, which on a string gives its character indices: the error
    // came out one letter per line, "messages.u", "messages.n", "messages.r".
    // Nothing here can make sense of that, so it falls through to the caller's
    // fallback message instead.
    if (arg && typeof arg === "object" && !Array.isArray(arg)) {
      for (const key of Object.keys(arg)) {
        let value = arg[key];
        if (value === null || value === undefined) {
          value = [];
        }

        if (Array.isArray(value)) {
          input.set(key, value);
        } else if (typeof value !== "string") {
          input.set(key, Messages.dotifyArray(value as string[]));
        } else {
          value = Messages.dotify("messages." + value);
          input.set(key, [value]);
        }
      }
    }
    return new Messages(input);
  }
  public static dotifyArray(value: any): string[] {
    if (value === null || value === undefined) {
      return [];
    }

    const messages = new Map<string, string[]>();
    Messages.extractMessages(value, messages);
    const withDots: string[] = [];

    for (const [_, arrayValue] of messages.entries()) {
      for (const singleValue of arrayValue) {
        withDots.push(Messages.dotify(singleValue));
      }
    }

    return withDots;
  }

  private static extractMessages(
    rawValue: any,
    messages: Map<string, string[]>
  ): void {
    for (const key of Object.keys(rawValue)) {
      const value = rawValue[key];
      if (value !== null && value !== undefined) {
        if (typeof value === "string") {
          messages.set(key, [value]);
        } else {
          Messages.extractMessages(value, messages);
        }
      }
    }
  }

  public static dotify(value: string): string {
    if (
      value === null ||
      value === undefined ||
      typeof value !== "string" ||
      value.trim().length === 0
    ) {
      return "";
    }
    const withDots = value.toLowerCase().replace(/\s/g, ".");
    return withDots;
  }

  public static isAMessages(toBeDetermined: any): toBeDetermined is Messages {
    if (toBeDetermined === undefined || toBeDetermined === null) {
      return false;
    }
    const maybeMessages = toBeDetermined as Messages;
    try {
      if (
        (toBeDetermined as Messages).messagesWithoutKeys &&
        (toBeDetermined as Messages).keysWithMessages
      ) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      const maptype = typeof maybeMessages.map;
      return false;
    }
  }

  public get hasAnyMessages(): boolean {
    if (this.map.size === 0) {
      return false;
    } else {
      let answer = false;
      this.map.forEach((value: string[]) => {
        if (value.length > 0) {
          answer = true;
        }
      });
      return answer;
    }
  }

  public add(key: string): void {
    this.map.set(key, [key]);
  }

  public get empty(): boolean {
    return !this.hasAnyMessages;
  }

  public get all(): Map<string, string[]> {
    const copy = new Map();
    this.map.forEach((value: string[], key: string) => {
      copy.set(key, value);
    });
    return copy;
  }

  public get keysWithMessages(): string[] {
    let answer: string[];
    answer = [];

    const debugMap = this.map;
    debugMap.forEach((value: string[], key: string) => {
      if (value !== null && value !== undefined && value.length > 0) {
        answer.push(key);
      }
    });

    return answer;
  }

  public get messagesWithoutKeys(): string[] {
    let answer: string[];
    answer = [];

    this.map.forEach((value) => {
      value.forEach((one) => {
        answer.push(one);
      });
    });

    return answer;
  }

  public hasMessagesFor(section: string): boolean {
    const found = this.map.get(section);
    return found !== null && found !== undefined && found.length > 0;
  }

  public emptyFor(section: string): boolean {
    const found = this.map.get(section);
    return found === null || found === undefined || found.length === 0;
  }

  public messagesFor(section: string): string[] {
    const empty: string[] = [];
    let answer = this.map.get(section);

    answer ??= empty;

    return answer!;
  }
  public merge(otherMessages: Messages, prefix?: string) {
    if (otherMessages) {
      otherMessages.all.forEach((value: string[], key: string) => {
        const allkey = (prefix ? prefix + "." : "") + key;
        if (value === null || value === undefined) {
          value = [];
        }
        let values = this.map.get(key);
        if (!values) {
          values = [];
          this.map.set(allkey, values);
        }
        value.forEach((line) => {
          values.push(line);
        });
      });
    }
  }
}
